import * as React from 'react'
import { cn } from '@/lib/utils'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  // `overflow-x-persistent` (src/index.css), not `overflow-auto`: macOS draws an
  // OVERLAY horizontal scrollbar that is invisible until a scroll is already in
  // progress, so a table wider than its container is indistinguishable from one
  // that has simply been cut off — and the column hiding past the right edge is
  // almost always the actions cell. Here rather than at each call site because
  // every shadcn table in the app scrolls through this one wrapper.
  //
  // `overflow-x: scroll` still reads as scrollable to `scrollsHorizontally` in
  // lib/stickyColumn, which is how a sticky column finds the element it is
  // pinned against; and overflow-y computes to auto, as `overflow-auto` gave.
  <div className='relative w-full overflow-x-persistent rounded-lg'>
    <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  </div>
))
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'bg-table-header [&_tr]:border-b [&_tr]:hover:bg-transparent',
      // A select-all checkbox paints its border and fill in `primary`, which is
      // the header's own blue — invisible here. Draw it in the header's text
      // colour instead: white outline, white fill with a blue tick when checked.
      '[&_[role=checkbox]]:border-table-header-foreground [&_[role=checkbox][data-state=checked]]:bg-table-header-foreground [&_[role=checkbox][data-state=checked]]:text-table-header [&_[role=checkbox][data-state=indeterminate]]:bg-table-header-foreground [&_[role=checkbox][data-state=indeterminate]]:text-table-header',
      className,
    )}
    {...props}
  />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
))
TableBody.displayName = 'TableBody'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className,
    )}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-3 text-left align-middle text-xs font-medium uppercase tracking-wide text-table-header-foreground [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('p-3 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell }
